import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { SkeletonIndicator } from '../../../components/skeleton-indicator.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { useDropzone } from 'react-dropzone';
import { isEditable } from '@testing-library/user-event/dist/utils';
import { start } from 'repl';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { purchaseOrderColumns } from './purchase-order-columns';
import { useFileDownload } from '../../../components/file-download';
import { UserPermissionName } from '../../../models/user-permission-name';
import { TabPanel } from '../../../components/tab-panel';
import { GuardedComponent } from '../../../components/guarded-component';
import { useAuthorize } from '../../../hooks/use-authorize';

interface ComponentCell {
  heading: string;
  value: any;
}

interface PurchaseOrderDialogProps {
  open: boolean;
  title?: string;
  workOrderData: any;
  workOrderID?: number;
  onClose: () => void;
  reloadData: () => void;
}

const styling = {
  headingStyle: { color: '#0000b5', paddingBottom: '10px' },
  labelStyle: { fontSize: '14px' },
  valueStyle: { color: '#000', fontWeight: '500', fontSize: '13px' },
  warningStyle: { color: '#ff7000', fontWeight: '500', fontSize: '13px' },
  dropZone: {
    padding: '18px 5px 17px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    marginRight: '10px',
  },
};

const ComponentCell = ({ heading, value }: ComponentCell) => {
  return (
    <Grid container spacing={0.5}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Typography variant="body1" color="textPrimary" style={styling.labelStyle}>
          {heading}
        </Typography>
      </Grid>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Typography variant="body1" color="text.secondary" style={styling.valueStyle} className="valueStyles">
          {value ? value : '--'}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const PurchaseOrderDialog = ({
  open,
  title,
  workOrderData,
  workOrderID,
  onClose,
  reloadData,
}: PurchaseOrderDialogProps) => {
  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();
  const [purchaseOrderId, setPurchaseOrderId] = useState<string>();
  const [PurchaseOrderNo, setPurchaseOrderNo] = useState<string>('');

  const [fileData, setFileData] = useState<any>({
    fileName: '',
    fileType: '',
  });
  const [file, setFile] = useState<File>();
  const [value, setValue] = useState('view');
  const [editItem, setEditItem] = useState<any>();

  const { isLoading: savePOLoading, mutateAsync } = useSaveData<any>(workOrdersApiKeyStore.updatePurchaseOrder(), () =>
    workOrdersApiKeyStore.getPurchaseOrderList(Number(workOrderID))
  );
  const { isError, mutateAsync: deletePO } = useDeleteData(
    workOrdersApiKeyStore.deletePurchaseOrder(!workOrderID ? '0' : workOrderID?.toString())
  );
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState({ severity: 'success', messageInfo: '' });

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const {
    data: purchaseOrder,
    refetch: refetchPurchaseOrder,
    isLoading,
  } = useLoadData<ApiResponse<any>>(workOrdersApiKeyStore.getPurchaseOrderList(Number(workOrderID)));
  const [totalPurchaseOrderNumbers, setTotalPurchaseOrderNumbers] = useState<string>('');
  const [messageForCharacterExceeds, setMessageForCharacterExceeds] = useState<string>('');
  const [isShowValidationMessage, setIsShowValidationMessage] = useState<boolean>(false);

  useEffect(() => {
    if (purchaseOrder && purchaseOrder?.data?.length > 0) {
      // const purchaseOrderNumbers = purchaseOrder?.data?.map((data: any) => data?.purchaseOrderNo).join(',');
      const purchaseOrderNumbers = purchaseOrder?.data
        ?.filter((data: any) => !data?.isEPOFile)
        ?.map((data: any) => data?.purchaseOrderNo)
        ?.join(',');

      setTotalPurchaseOrderNumbers(purchaseOrderNumbers);
    } else {
      setTotalPurchaseOrderNumbers('');
    }
  }, [purchaseOrder]);

  const handlePurchaseOrderChange = (value: string) => {
    const cleanedValue = value;

    if (totalPurchaseOrderNumbers?.length > 31) {
      setMessageForCharacterExceeds(
        'You’ve reached the 31 character limit to send the Purchase Order number for QuickBooks Invoicing.(Note:If entering multiple Purchase Order numbers, commas are included in the 31-character limit for QuickBooks Invoicing)'
      );
      setIsShowValidationMessage(true);
    } else if (
      totalPurchaseOrderNumbers?.length !== 0 &&
      totalPurchaseOrderNumbers?.length + cleanedValue?.length > 30
    ) {
      setMessageForCharacterExceeds(
        'You’ve reached the 31 character limit to send the Purchase Order number for QuickBooks Invoicing.(Note:If entering multiple Purchase Order numbers, commas are included in the 31-character limit for QuickBooks Invoicing)'
      );
      setIsShowValidationMessage(true);
    } else if (totalPurchaseOrderNumbers?.length === 0 && cleanedValue.length > 31) {
      setMessageForCharacterExceeds(
        'You’ve reached the 31 character limit to send the Purchase Order number for QuickBooks Invoicing.'
      );
      setIsShowValidationMessage(true);
    } else {
      setPurchaseOrderNo(cleanedValue);
      setIsShowValidationMessage(false);
    }
  };

  const uploadPurchaseOrder = async () => {
    if (!file) {
      setIsOpen(true);
      setMessage({ severity: 'error', messageInfo: 'please choose file' });
    } else if (workOrderData?.requirePurchaseOrder && !PurchaseOrderNo) {
      setIsOpen(true);
      setMessage({
        severity: 'error',
        messageInfo: value === 'uploadEPO' ? 'EPO name is required' : 'Purchase order number is required',
      });
    } else {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('FileName', file?.name);
      formData.append('FileType', file?.type);
      formData.append('PurchaseOrderNo', PurchaseOrderNo?.trim() ?? '');
      formData.append('isEPOFile', (value === 'uploadEPO').toString());

      if (workOrderID) formData.append('WorkOrderID', workOrderID.toString());
      await mutateAsync({
        data: formData,
      });
      reloadData();
      onClose();
      setTotalPurchaseOrderNumbers('');
      setMessageForCharacterExceeds('');
      setIsShowValidationMessage(false);
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setMessage({ severity: 'success', messageInfo: '' });

    setIsOpen(false);
  };

  const deletePurchaseOrder = async () => {
    await deletePO();
    reloadData();
  };

  const handleDownloadFile = async (data: any) => {
    const value = { ...data, purchaseOrderID: data?.purchaseOrderID };

    downloadFile({ additionalData: value });
  };

  const handleTabsChange = (event: SyntheticEvent, newValue: string) => {
    if (newValue === 'view') {
      setFile(undefined);
      setPurchaseOrderId('');
      setPurchaseOrderNo('');
      setFileData((prevState: any) => ({
        ...prevState,
        fileName: '',
        fileType: '',
      }));
      setEditItem(undefined);
    }
    setValue(newValue);
    setIsShowValidationMessage(false);
  };

  const updatePurchaseOrder = async () => {
    const formData = new FormData();
    formData.append('file', file ?? '');
    formData.append('FileName', file ? file?.name : fileData?.fileName);
    formData.append('FileType', file ? file?.type : fileData?.fileType);
    formData.append('PurchaseOrderID', purchaseOrderId ?? '');
    formData.append('PurchaseOrderNo', PurchaseOrderNo?.trim() ?? '');
    if (workOrderID) formData.append('WorkOrderID', workOrderID.toString());
    await mutateAsync({
      data: formData,
    });
    setFile(undefined);
    setPurchaseOrderId('');
    setPurchaseOrderNo('');
    setFileData((prevState: any) => ({
      ...prevState,
      fileName: '',
      fileType: '',
    }));
    setEditItem(undefined);
    setValue('view');
  };

  const handleEdit = (value: any) => {
    setPurchaseOrderId(value?.purchaseOrderID);
    setPurchaseOrderNo(value?.purchaseOrderNo);
    setFileData((prevState: any) => ({
      ...prevState,
      fileName: value?.fileName,
      fileType: value?.fileType,
    }));
    setEditItem(value);

    // totalPurchaseOrderNumbers - value?.purchaseOrderNo;
    const updatedPurchaseOrderNumbers = totalPurchaseOrderNumbers
      ?.split(',')
      .map((po) => po.trim())
      .filter((po) => po && po !== value?.purchaseOrderNo?.trim())
      .join(',');

    const finalValue = updatedPurchaseOrderNumbers ? `${updatedPurchaseOrderNumbers},` : '';

    setTotalPurchaseOrderNumbers(finalValue);
    setValue('update');
  };

  const FileDownloadElement = (dataItem: any) => {
    const fileURL = dataItem?.dataItem?.purchaseOrderUrl;
    return (
      fileURL && (
        <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_PO_WORKORDER]}>
          <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(dataItem?.dataItem)}>
            <Tooltip title={<Typography variant="body1">Download</Typography>}>
              <DownloadIcon className="btnEdit" />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
      )
    );
  };

  const isEditPruchaseOrder = useAuthorize([UserPermissionName.EDIT_PO_WORKORDER]);
  const isAddPruchaseOrder = useAuthorize([UserPermissionName.UPLOAD_PO_WORKORDER]);
  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'lg'}
      // onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 2, pt: 1, pb: 1, m: 0 }}>
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <>
        <DialogContent dividers className="popupDilog">
          <LoadingIndicator isLoading={savePOLoading} isSpinner />

          <Grid container>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Customer" value={workOrderData?.customerName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Office" value={workOrderData?.officeName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Project" value={workOrderData?.projectName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Lot/Unit#" value={workOrderData?.unitName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Address" value={workOrderData?.officeName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Service" value={workOrderData?.serviceName} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              <ComponentCell heading="Work Order#" value={workOrderData?.workOrderNumber} />
            </Grid>
            <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
              {/* <ComponentCell heading="Forecast Date" value={workOrderData?.forecastedDate} />*/}
            </Grid>
          </Grid>
          <Tabs value={value} onChange={handleTabsChange} style={{ borderBottom: '1px solid #ccc' }}>
            <Tab value="view" label="View" />
            <Tab value="uploadPO" label="Upload PO" hidden={!!editItem || !isAddPruchaseOrder} />
            <Tab
              value="uploadEPO"
              label="Upload EPO"
              hidden={!!editItem || !isAddPruchaseOrder || !workOrderData?.showEPOTab}
            />
            <Tab value="update" label="Update" hidden={!editItem || !isEditPruchaseOrder} />
          </Tabs>
          <TabPanel selectedValue={value} value="view">
            <Grid style={{ padding: '15px 0' }}>
              {isLoading && <LoadingIndicator isLoading={isLoading} />}
              <SiTrackGrid
                fileTitle='purchase_order'
                dataItemKey="purchaseOrderID"
                columns={purchaseOrderColumns}
                // queryConfig={workOrdersApiKeyStore.getPurchaseOrderList(Number(workOrderID))}
                dataItems={purchaseOrder?.data || []}
                CustomComponent={FileDownloadElement}
                deleteMutationConfig={
                  workOrderData?.canEdit
                    ? (item) => workOrdersApiKeyStore.deletePurchaseOrder(item.purchaseOrderID)
                    : undefined
                }
                actionPermissions={{
                  edit: UserPermissionName.EDIT_PO_WORKORDER,
                  delete: UserPermissionName.DELETE_PO_WORKORDER,
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                onEdit={handleEdit}
                isBusy={isFileDownloadLoading}
                onDeletedItems={() => {
                  reloadData();
                  refetchPurchaseOrder();
                }}
              />
            </Grid>
          </TabPanel>
          <TabPanel selectedValue={value} value="uploadPO">
            <Grid container style={{ padding: '15px 0' }}>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <TextField
                  type="text"
                  className="w-100"
                  style={{ paddingRight: '10px' }}
                  label="Purchase Order No"
                  name="PurchaseOrderNo"
                  value={PurchaseOrderNo}
                  onChange={(event: any) => handlePurchaseOrderChange(event.target.value)}
                />
                {isShowValidationMessage && <span style={{ color: 'red' }}>{messageForCharacterExceeds}</span>}
              </Grid>
              <Grid item lg={6} sm={12} sx={{ mb: 2 }}>
                <div style={styling.dropZone} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p>Drop the files here ...</p>
                  ) : (
                    <div>
                      {file ? (
                        <p>{`${file.name} are selected to upload`}</p>
                      ) : (
                        <p>Drag and drop a file here, or click to select a file</p>
                      )}
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                  <Button
                    disabled={!workOrderData?.canEdit || isReadOnlyPermission}
                    variant="contained"
                    onClick={uploadPurchaseOrder}
                    style={{ padding: '15px' }}
                  >
                    Upload Purchase Order
                  </Button>
                </GuardedComponent>
              </Grid>
              {!workOrderData?.requirePurchaseOrder && (
                <Grid style={{ padding: '0 0 10px 0' }}>
                  <Typography variant="body1" color="text.warning" style={styling.warningStyle}>
                    Note: Purchase order is optional for this work order
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>
          <TabPanel selectedValue={value} value="uploadEPO">
            <Grid container style={{ padding: '15px 0' }}>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <TextField
                  type="text"
                  className="w-100"
                  style={{ paddingRight: '10px' }}
                  label="EPO Name"
                  name="epoName"
                  value={PurchaseOrderNo}
                  onChange={(event: any) => handlePurchaseOrderChange(event.target.value)}
                />
                {isShowValidationMessage && <span style={{ color: 'red' }}>{messageForCharacterExceeds}</span>}
              </Grid>
              <Grid item lg={6} sm={12} sx={{ mb: 2 }}>
                <div style={styling.dropZone} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p>Drop the files here ...</p>
                  ) : (
                    <div>
                      {file ? (
                        <p>{`${file.name} are selected to upload`}</p>
                      ) : (
                        <p>Drag and drop a file here, or click to select a file</p>
                      )}
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                  <Button
                    disabled={!workOrderData?.canEdit || isReadOnlyPermission}
                    variant="contained"
                    onClick={uploadPurchaseOrder}
                    style={{ padding: '15px' }}
                  >
                    Upload Purchase Order
                  </Button>
                </GuardedComponent>
              </Grid>
              {!workOrderData?.requirePurchaseOrder && (
                <Grid style={{ padding: '0 0 10px 0' }}>
                  <Typography variant="body1" color="text.warning" style={styling.warningStyle}>
                    Note: Purchase order is optional for this work order
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>
          <TabPanel selectedValue={value} value="update">
            <Grid container style={{ padding: '15px 0' }}>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <TextField
                  type="text"
                  className="w-100"
                  style={{ paddingRight: '10px' }}
                  label="Purchase Order No"
                  name="PurchaseOrderNo"
                  value={PurchaseOrderNo}
                  onChange={(event: any) => handlePurchaseOrderChange(event.target.value)}
                />
                {isShowValidationMessage && <span style={{ color: 'red' }}>{messageForCharacterExceeds}</span>}
              </Grid>
              <Grid item lg={6} sm={12} sx={{ mb: 2 }}>
                <div style={styling.dropZone} {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p>Drop the files here ...</p>
                  ) : (
                    <div>
                      {file ? (
                        <p>{`${file.name} are selected to upload`}</p>
                      ) : (
                        <p>{`${fileData?.fileName} are selected to upload`}</p>
                      )}
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item lg={3} sm={12} sx={{ mb: 2 }}>
                <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                  <Button
                    disabled={!workOrderData?.canEdit || isReadOnlyPermission}
                    variant="contained"
                    onClick={updatePurchaseOrder}
                    style={{ padding: '15px' }}
                  >
                    Update Purchase Order
                  </Button>
                </GuardedComponent>
              </Grid>
              {!workOrderData?.requirePurchaseOrder && (
                <Grid style={{ padding: '0 0 10px 0' }}>
                  <Typography variant="body1" color="text.warning" style={styling.warningStyle}>
                    Note: Purchase order is optional for this work order
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions style={{ padding: ' 15px' }}>
          <Button variant="outlined" onClick={onClose} style={{ marginRight: 'auto' }}>
            {' '}
            Cancel
          </Button>
          <Snackbar
            open={isOpen}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClose={handleClose}
          >
            <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={handleClose}>
              {!message.messageInfo ? ' ' : message?.messageInfo}
            </Alert>
          </Snackbar>
        </DialogActions>
      </>
    </Dialog>
  );
};
